// src/lib/runInAI.js
// Utility for "Run in AI" feature — builds platform URLs with prompt pre-filled
// and recommends the best platform based on entry model/type data

export const AI_PLATFORMS = [
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    icon: '🤖',
    color: '#19c37d',
    bg: 'rgba(25,195,125,0.1)',
    border: 'rgba(25,195,125,0.35)',
    buildUrl: (prompt) => `https://chatgpt.com/?q=${encodeURIComponent(prompt)}`,
    // Model name fragments that suggest this platform
    modelHints: ['gpt', 'openai', 'chatgpt', 'o1', 'o3', 'o4'],
  },
  {
    id: 'claude',
    name: 'Claude',
    icon: '✦',
    color: '#d97706',
    bg: 'rgba(217,119,6,0.1)',
    border: 'rgba(217,119,6,0.35)',
    buildUrl: (prompt) => `https://claude.ai/new?q=${encodeURIComponent(prompt)}`,
    modelHints: ['claude', 'anthropic', 'sonnet', 'opus', 'haiku'],
  },
  {
    id: 'gemini',
    name: 'Gemini',
    icon: '♊',
    color: '#4285f4',
    bg: 'rgba(66,133,244,0.1)',
    border: 'rgba(66,133,244,0.35)',
    buildUrl: (prompt) => `https://gemini.google.com/app?q=${encodeURIComponent(prompt)}`,
    modelHints: ['gemini', 'google', 'bard'],
  },
  {
    id: 'perplexity',
    name: 'Perplexity',
    icon: '🔍',
    color: '#20b2aa',
    bg: 'rgba(32,178,170,0.1)',
    border: 'rgba(32,178,170,0.35)',
    buildUrl: (prompt) => `https://www.perplexity.ai/?q=${encodeURIComponent(prompt)}`,
    modelHints: ['perplexity', 'sonar'],
    // Recommend for research/reference-heavy prompts
    typeHints: ['prompt'],
  },
  {
    id: 'grok',
    name: 'Grok',
    icon: '𝕏',
    color: '#e7e9ea',
    bg: 'rgba(231,233,234,0.08)',
    border: 'rgba(231,233,234,0.25)',
    buildUrl: (prompt) => `https://grok.com/?q=${encodeURIComponent(prompt)}`,
    modelHints: ['grok', 'xai', 'x.ai'],
  },
  {
    id: 'copilot',
    name: 'Copilot',
    icon: '🪟',
    color: '#0078d4',
    bg: 'rgba(0,120,212,0.1)',
    border: 'rgba(0,120,212,0.35)',
    buildUrl: (prompt) => `https://copilot.microsoft.com/?q=${encodeURIComponent(prompt)}`,
    modelHints: ['copilot', 'microsoft', 'bing', 'azure'],
    // Recommend for code type
    typeHints: ['code'],
  },
  {
    id: 'higgsfield',
    name: 'Higgsfield',
    icon: '🎬',
    color: '#a855f7',
    bg: 'rgba(168,85,247,0.1)',
    border: 'rgba(168,85,247,0.35)',
    buildUrl: (prompt) => `https://app.higgsfield.ai/`,  // no URL param support yet
    modelHints: ['higgsfield'],
    // Only recommend for video type
    typeHints: ['video'],
    noPromptInject: true,  // copies to clipboard instead of URL param
  },
];

/**
 * Recommends the best platform based on entry model and type.
 * Returns the platform id string, or 'chatgpt' as default.
 */
export function recommendPlatform(model = '', type = '') {
  const m = (model || '').toLowerCase();
  const t = (type || '').toLowerCase();

  // Check model name first — most reliable signal
  for (const platform of AI_PLATFORMS) {
    if (platform.modelHints?.some(hint => m.includes(hint))) {
      return platform.id;
    }
  }

  // Fall back to type hints
  for (const platform of AI_PLATFORMS) {
    if (platform.typeHints?.some(hint => t === hint)) {
      return platform.id;
    }
  }

  // Default
  return 'chatgpt';
}

/**
 * Returns true if the user is on a mobile/tablet device.
 * Used to decide whether to copy-then-open instead of URL-inject.
 */
export function isMobileDevice() {
  if (typeof window === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
    navigator.userAgent
  );
}

/**
 * Opens a platform with the prompt pre-loaded.
 * On mobile: always copies to clipboard first (native apps ignore ?q= param).
 * On desktop: uses URL param injection where supported.
 * For platforms with noPromptInject (e.g. Higgsfield): always copies.
 */
export function runInPlatform(platformId, prompt) {
  const platform = AI_PLATFORMS.find(p => p.id === platformId);
  if (!platform) return;

  const mobile = isMobileDevice();
  const shouldCopy = platform.noPromptInject || mobile;

  // Open the new tab FIRST — must be synchronous and directly in the
  // user gesture call stack so mobile browsers don't block it as a popup.
  // On mobile, _blank opens a new tab in Safari/Chrome, keeping CPG open.
  const url = platform.buildUrl(prompt);
  const newTab = window.open(url, '_blank', 'noopener,noreferrer');

  // If browser blocked the popup (some Android in-app browsers), fall back
  // to creating a link and programmatically clicking it
  if (!newTab) {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  // Copy AFTER opening so the async clipboard write doesn't delay the open
  if (shouldCopy) {
    navigator.clipboard?.writeText(prompt).catch(() => {});
  }

  return { copied: shouldCopy, mobile };
}
